import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Addon, Item, MenuResponse } from "../../../api/menu";
import { getExceededLunchLimits } from "../../../api/lunchMenus";
import {
  lunchCountersOf,
  lunchKeyOfItem,
  lunchPairKey,
  productOfLunchPairKey,
  type LunchCounters,
} from "../../../lib/lunchCatalog";
import {
  lunchMaxLineQuantity,
  lunchRoomOfKey,
  lunchUsageByKey,
  type PortionCartLine,
} from "../../../lib/lunchPortions";
import { getItemName } from "../../../utils/translations";
import type { useOrderingMessage } from "../components/useOrderingMessage";

interface LunchLimitsOptions {
  allItems: Item[];
  allAddons: Addon[];
  counters: LunchCounters | undefined;
  orderItems: PortionCartLine[];
  message: ReturnType<typeof useOrderingMessage>;
}

/**
 * Порциите на ден от дневните менюта в „Маркиране" (BE
 * `bills/service_lunch_limits.py`) — и на основните, и на добавките; всяко меню
 * брои своите. Каталогът знае колко остават към последното меню; тук кошницата не може
 * да вземе повече, а сервитьорът разбира защо. Истината е сървърът: двама,
 * взели последната порция едновременно — вторият получава 409 `lunch_limit_reached`.
 */
export const useLunchLimits = ({ allItems, allAddons, counters, orderItems, message }: LunchLimitsOptions) => {
  const { t, i18n } = useTranslation();
  const itemsById = useMemo(() => new Map(allItems.map((item) => [item.id, item])), [allItems]);
  const addonsById = useMemo(() => new Map(allAddons.map((addon) => [addon.id, addon])), [allAddons]);
  const cartUsage = useMemo(
    () => lunchUsageByKey(orderItems, itemsById, addonsById, counters),
    [orderItems, itemsById, addonsById, counters],
  );

  const nameOfProduct = (productId: number) => {
    const item = itemsById.get(productId);
    return item ? getItemName(item.itemLangs, i18n.language, item.slug) : `#${productId}`;
  };

  const warn = (productId: number, left: number) =>
    message.warning(
      left <= 0
        ? t("lunchMenu.limitSoldOut", { name: nameOfProduct(productId) })
        : t("lunchMenu.limitOnlyLeft", { name: nameOfProduct(productId), count: left }),
    );

  /** Колко още от продукта в каталога (копие от дневно меню) — с цялата кошница. */
  const roomOf = (item: Pick<Item, "lunchMenuId" | "sourceItemId">) =>
    Math.max(0, lunchRoomOfKey(counters, cartUsage, lunchKeyOfItem(item)));

  /** Порциите по брояч на произволна кошница (напр. без реда в редакция). */
  const usageOf = (cart: PortionCartLine[]) =>
    cart === orderItems ? cartUsage : lunchUsageByKey(cart, itemsById, addonsById, counters);

  /**
   * Колко от `wanted` порции на основния продукт могат да се добавят (с
   * кошницата). Не стигат ли — казва колко остават; 0 → изчерпано.
   */
  const allowedQuantity = (itemId: number, wanted: number) => {
    const item = itemsById.get(itemId);
    const key = item && lunchKeyOfItem(item);
    if (!item || !key) return wanted;
    const room = lunchRoomOfKey(counters, cartUsage, key);
    if (wanted <= room) return wanted;
    warn(item.sourceItemId ?? itemId, Math.max(0, room));
    return Math.max(0, room);
  };

  /**
   * Не стигат ли порциите за `nextCart` (основни + добавки): спира само ръста
   * спрямо `currentCart` — брояч, обновен от друг терминал, не блокира
   * промяна, която не добавя от него. true = не стига (казано е на сервитьора).
   */
  const cartShortage = (nextCart: PortionCartLine[], currentCart: PortionCartLine[] = orderItems) => {
    if (!counters) return false;
    const next = lunchUsageByKey(nextCart, itemsById, addonsById, counters);
    const now = currentCart === orderItems ? cartUsage : lunchUsageByKey(currentCart, itemsById, addonsById, counters);
    for (const [key, used] of next) {
      const left = counters.leftByKey[key];
      if (left == null || used <= (now.get(key) ?? 0) || used <= left) continue;
      warn(productOfLunchPairKey(key), Math.max(0, left - (now.get(key) ?? 0)));
      return true;
    }
    return false;
  };

  /**
   * Най-голямата бройка в „Промени" за ред от дневно меню с тези добавки —
   * спрямо `baseCart` (кошницата без самия ред; повторна поръчка — празна).
   */
  const maxLineQuantity = (
    item: Item | null | undefined,
    addons: PortionCartLine["addons"],
    baseCart: PortionCartLine[] = orderItems,
  ) =>
    lunchMaxLineQuantity(item, addons, addonsById, counters, usageOf(baseCart));

  /**
   * Колко остават от опция на добавка за ред от дневно меню — спрямо
   * `baseCart` (кошницата без самия ред). Infinity — без порции.
   */
  const optionRoomOf = (
    item: Item | null | undefined,
    optionId: number,
    baseCart: PortionCartLine[] = orderItems,
  ) => {
    if (!item || item.lunchMenuId == null || !counters) return Infinity;
    return lunchRoomOfKey(counters, usageOf(baseCart), lunchPairKey(item.lunchMenuId, optionId));
  };

  /** „Поръчай пак" на ред от дневно меню: изчерпан ли е по свежото меню (и казва го). */
  const isSoldOutFresh = (menu: MenuResponse | undefined, lunchMenuId: number, productId: number) => {
    const left = lunchCountersOf(menu?.lunchMenus ?? []).leftByKey[lunchPairKey(lunchMenuId, productId)];
    if (left == null || left > 0) return false;
    warn(productId, 0);
    return true;
  };

  /** Текстът за 409 `lunch_limit_reached`, иначе null. */
  const limitErrorMessage = (error: unknown) => {
    const exceeded = getExceededLunchLimits(error);
    if (exceeded == null) return null;
    const items = exceeded
      .map((entry) =>
        t("lunchMenu.limitReachedItem", { name: nameOfProduct(entry.itemId), count: entry.remaining }),
      )
      .join(", ");
    return t("lunchMenu.limitReachedOrder", { items });
  };

  return {
    roomOf,
    allowedQuantity,
    cartShortage,
    maxLineQuantity,
    optionRoomOf,
    usageOf,
    warn,
    isSoldOutFresh,
    limitErrorMessage,
    cartUsage,
  };
};
